// audioDiagCounters — CU-869dafa5q.
//
// Tiny in-memory counter registry that every diagnostic logger bumps when
// it emits an event of interest. `audio_anomaly_summary` reads + resets
// these counters to give an investigator a one-shot "what was happening
// when the chop started?" payload without having to query every individual
// event family.
//
// Counters are module-level singletons — there's at most one active call
// at a time on this page, so the counter scope matches the diagnostic
// window of interest (between the most recent chop_classified and either
// chop_resolved or call end).

export const COUNTER_KEYS = [
    'workletHeartbeatGap',
    'workletHeartbeatSeqSkip',
    'workletProcessorError',
    'contextClockGap',
    'mainThreadTimerGap',
    'workletContextResume',
    'longTaskOver200ms',
    'systemClockJump',
    'mediaDevicesChange',
    'networkChange',
    'playRejected',
    'trackMute',
    'trackEnded',
    'pcConnectionFailed',
    'pcDisconnected',
    'pageFreeze',
    'pageResume',
    'audioElementStalled',
    'audioElementWaiting',
    'selectedCandidatePairChange',
    'memoryPressureCrossed',
] as const;
export type CounterKey = (typeof COUNTER_KEYS)[number];

type Counters = Record<CounterKey, number>;

function emptyCounters(): Counters {
    const result = {} as Counters;
    COUNTER_KEYS.forEach((k) => { result[k] = 0; });
    return result;
}

let counters: Counters = emptyCounters();
let counterWindowStartWallMs: number = Date.now();

export function bumpDiagCounter(key: CounterKey): void {
    counters[key]++;
}

export function snapshotDiagCounters(): {windowMs: number; counters: Counters; dominantFailureHint: string | null} {
    const now = Date.now();
    const windowMs = now - counterWindowStartWallMs;
    // Pick a coarse "dominant failure hint" from whichever counter is
    // non-trivially highest. Helps the investigator's first read.
    let maxKey: CounterKey | null = null;
    let maxVal = 0;
    (Object.keys(counters) as CounterKey[]).forEach((k) => {
        if (counters[k] > maxVal) {
            maxVal = counters[k];
            maxKey = k;
        }
    });
    return {
        windowMs,
        counters: {...counters},
        dominantFailureHint: maxVal >= 2 ? maxKey : null,
    };
}

export function resetDiagCounters(): void {
    counters = emptyCounters();
    counterWindowStartWallMs = Date.now();
}

// Page-level loggers that fire continuously (memory pressure, user
// activity proxy) need an active-call gate so they don't dominate the
// telemetry stream when no call is happening. The flag lives in this
// shared module because both audioCallLogger and audioPageLogger import
// it, and adding a separate module just for one bool seems excessive.
let callActive = false;
export function setAudioCallActive(active: boolean): void {
    callActive = active;
}
export function isAudioCallActive(): boolean {
    return callActive;
}

// ─── Diagnostics kill switch ────────────────────────────────────────────
//
// Off by default = enabled. Operators can disable the entire diagnostic
// emit set (periodic ticks, getStats polls, lifecycle listener fan-out)
// without a deploy:
//
//   - URL flip: `?diag=off` disables and stores in localStorage; `?diag=on`
//     re-enables and stores; `?diag=auto` clears the stored override.
//   - localStorage sticky: `phq_audio_diag` = `'on'` | `'off'`.
//   - Default when nothing's set: enabled.
//
// CORE variant-d behavior (the AudioWorklet attachment, the AudioContext
// resume hooks, the SDP munging, the play() wrapper itself) is NOT
// affected — only the diagnostic emit/poll layer. The kill switch is
// checked once at start*() time in each diagnostic module; once a tick
// has been started, flipping the switch mid-call won't stop it (next
// call honours the new value). That keeps the gate cheap to check.
//
// Logs `audio_diagnostics_disabled` once per page-load when the kill
// switch is set, so investigators looking at a thin log stream can
// tell whether it's "no chop happened" or "diagnostics off".

const DIAG_STORAGE_KEY = 'phq_audio_diag';

let urlOverrideProcessed = false;
// 'on'/'off' = URL set value AND storage write failed; 'clear' = URL
// requested auto AND storage removal failed (suppress stale stored
// value); null = storage write succeeded or no URL override.
let urlOverrideInMemory: 'on' | 'off' | 'clear' | null = null;
let diagDisabledLoggedThisPage = false;

function readDiagFlagFromUrl(): 'on' | 'off' | 'auto' | null {
    try {
        const raw = new URLSearchParams(window.location.search).get('diag');
        if (!raw) return null;
        const v = raw.toLowerCase();
        if (v === 'on' || v === 'off' || v === 'auto') return v;
    } catch { /* swallow */ }
    return null;
}

function readDiagFlagFromStorage(): 'on' | 'off' | null {
    try {
        const v = window.localStorage.getItem(DIAG_STORAGE_KEY);
        if (v === 'on' || v === 'off') return v;
    } catch { /* swallow */ }
    return null;
}

function writeDiagFlagToStorage(v: 'on' | 'off' | null): boolean {
    try {
        if (v == null) window.localStorage.removeItem(DIAG_STORAGE_KEY);
        else window.localStorage.setItem(DIAG_STORAGE_KEY, v);
        return true;
    } catch {
        return false;
    }
}

// One-shot URL processing — `?diag=on|off` writes to localStorage so the
// preference sticks across reloads; `?diag=auto` clears the sticky. We
// process the URL once per page load (subsequent reads are pure
// localStorage), then `isAudioDiagEnabled()` returns the current
// localStorage value live on every call. That way an operator flipping
// the localStorage key in DevTools mid-investigation actually takes
// effect on the next call without a page reload — matching the contract
// documented for the kill switch.
function processUrlOverrideOnce(): void {
    if (urlOverrideProcessed) return;
    urlOverrideProcessed = true;
    const url = readDiagFlagFromUrl();
    if (url === 'auto') {
        const ok = writeDiagFlagToStorage(null);
        urlOverrideInMemory = ok ? null : 'clear';
    } else if (url === 'on' || url === 'off') {
        const ok = writeDiagFlagToStorage(url);
        urlOverrideInMemory = ok ? null : url;
    }
}

export function isAudioDiagEnabled(): boolean {
    processUrlOverrideOnce();
    if (urlOverrideInMemory === 'on' || urlOverrideInMemory === 'off') {
        return urlOverrideInMemory === 'on';
    }
    // 'clear' = ?diag=auto requested but removeItem failed. Suppress
    // stale stored value and apply default-enabled.
    if (urlOverrideInMemory === 'clear') {
        return true;
    }
    const stored = readDiagFlagFromStorage();
    if (stored === 'on' || stored === 'off') return stored === 'on';
    return true; // default
}

/** Test-only: reset URL one-shot + once-flag so the next call re-reads. */
export function __resetDiagEnabledCacheForTest(): void {
    urlOverrideProcessed = false;
    urlOverrideInMemory = null;
    diagDisabledLoggedThisPage = false;
}

/**
 * Returns `true` exactly once per page load if diagnostics are disabled,
 * so each diagnostic module can log a single audit-trail event without
 * coordinating.
 */
export function shouldLogDiagDisabledOnce(): boolean {
    if (isAudioDiagEnabled()) return false;
    if (diagDisabledLoggedThisPage) return false;
    diagDisabledLoggedThisPage = true;
    return true;
}

// ─── Diagnostic-log shims ───────────────────────────────────────────────
//
// Codex review — the start*() gates suppress the new periodic/event-driven
// emit sites, but `backgroundedAudioWorklet` interleaves CORE behavior
// (ensureRunning / page-lifecycle listeners that keep AudioContext alive)
// with DIAGNOSTIC emits (audio_worklet_context_resumed, audio_page_freeze,
// etc.). Splitting them with a per-site `if (isAudioDiagEnabled())` is
// noisy and error-prone — instead, use these shims at the emit site:
// CORE events keep using `logger.info`, DIAGNOSTIC events use `diagLogInfo`.
//
// Avoiding a dependency on the logger module here — accept any logger-like
// shape so consumers can pass their imported `logger` instance through.

type LoggerLike = {
    info: (msg: string, ctx?: Record<string, unknown>) => void;
    warn: (msg: string, ctx?: Record<string, unknown>) => void;
};

export function diagLogInfo(log: LoggerLike, msg: string, ctx?: Record<string, unknown>): void {
    if (!isAudioDiagEnabled()) return;
    log.info(msg, ctx);
}

export function diagLogWarn(log: LoggerLike, msg: string, ctx?: Record<string, unknown>): void {
    if (!isAudioDiagEnabled()) return;
    log.warn(msg, ctx);
}
